// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 06997ef0-9367-4f4f-985b-9f3d1b25fb7d

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Naming dictionary — decoding the client's private hostname vocabulary
// ---------------------------------------------------------------------------
// Every health system encodes its own vocabulary in host names. One estate says
// PRD, the next says P, the next says nothing at all and puts the workload in
// the cluster name. A server called EPICHSWEB301 is only "Epic Hyperspace Web,
// 300-series" to somebody who already knows that HSW means Hyperspace Web and
// what the 300 block is for. Decoding that vocabulary is what turns thousands of
// anonymous rows into a mapped estate.
//
// Because the vocabulary differs per client, it is LEARNED and STORED per
// tenant (localStorage `primordia.naming.<tenantId>`), never hardcoded. The only
// universal part is the Epic component vocabulary (HSW, EPS, INTCON, …), and
// even that is offered as a SUGGESTION TO CONFIRM — a token means whatever the
// client says it means, and a client is free to have reused HSW for something
// else entirely.
//
// WHAT THIS SCREEN IS NOT: it is not a mapper. ServerMapping.js owns the rule
// engine, the token matcher, the preview/apply/revert workflow and every write
// to a fact sheet. This screen FEEDS that one: it imports ServerMapping's
// tokenizer and matcher rather than re-implementing them (a second, subtly
// different tokeniser would be the single most dangerous thing we could add),
// and its output is a set of rule definitions in ServerMapping's own shape,
// copied or exported. Nothing here writes a fact sheet.
//
// MEASURED GROUND TRUTH from a live 7,305-server estate, which is why the copy
// reads the way it does:
//   * only 782 servers (10.7%) carry ANY recognisable environment token in the
//     name; 6,523 carry none. Hostname decoding will never map the estate on its
//     own — the dictionary SUPPLEMENTS cluster/datacenter, it does not replace
//     them.
//   * datacenter was populated on 7,305 / 7,305 and cluster on 7,295, with
//     values (MDC-CITRIX, ART-CITRIX-EPIC, usb-cluster04) that literally name
//     the workload. So tokens found in cluster/datacenter are first-class
//     dictionary material, not an afterthought.
//   * 1,911 servers were Epic-prefixed and fell into decodable families
//     (epicart 847, epicmdc 809, epichsweb 154, epiceps 26, …).
//   * 259 Epic servers ended in a three-digit block (3xx 156, 2xx 52, 1xx 31,
//     0xx 20) and NOT ONE of them carried an unambiguous environment word. The
//     meaning of that leading digit is therefore NOT DERIVABLE from the data. It
//     is a detected pattern, not a decoded one, and this screen turns it into a
//     question for the client instead of a guess.
//   Every number rendered below is computed live from the tenant in front of
//   you; the figures above are only why the screen exists.
//
// HONESTY POSTURE (same as ServerMapping.js / AiEnrichment.js):
//   * a detected pattern is always "seen N times, meaning unknown" until a human
//     supplies the meaning — the word "unknown" is on screen, not implied;
//   * no suggested Epic token is ever auto-confirmed, not even one with a
//     perfect count;
//   * the coverage tab leads with the UNEXPLAINED remainder, never with the
//     flattering percentage;
//   * undecodable patterns become an exportable question list, because "what do
//     the 100/200/300 series mean?" is exactly what a consultant should walk into
//     the next meeting holding;
//   * client-specific tokens (site codes) are tenant-scoped and cannot be pushed
//     to the cross-tenant library — those are client facts and must not leak.
//
// PERFORMANCE: 7,305 names are tokenised ONCE into an index, and every count on
// every tab is derived inside useMemo from that index. Nothing iterates the
// estate during render; tables are capped and the full set is always exportable.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress,
  Divider, FormControlLabel, IconButton, MenuItem, Paper, Stack, Switch, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { PICKLISTS } from './schema';
import { exportCsv, triggerDownload } from './exports';
import HelpBubble from './HelpBubble';
import store from './store';
// Imported, never re-implemented: `nameTokens` splits on delimiters, camelCase
// boundaries and letter<->digit transitions; `hasToken` does exact token
// equality (with an opt-in suffix mode) so "dr" can never match SQLDRIVER.
import { hasToken, isServerLike, nameTokens } from './ServerMapping';
import { TabLabel } from './ui';

// Optimum palette
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const RED = '#C62828';
const AMBER = '#B26A00';
const GREEN = '#2E7D32';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD, whiteSpace: 'nowrap' } };

// Render caps. Every count and every export always covers the full set; these
// only limit how many <TableRow>s exist at once.
const PATTERN_CAP = 40;
const SAMPLE_ROWS = 150;
const EXAMPLES = 5;

// Per-tenant vocabulary. The general (cross-client) library lives under its own
// key so a site code can never ride along into another workspace.
const LS_DICT = (t) => `primordia.naming.${t || 'default'}`;
const LS_GENERAL = 'primordia.naming.general';
// Shown to the user on the Apply tab so they can see exactly where generated
// rules belong. This screen never writes to it.
const LS_SERVER_RULES = (t) => `primordia.serverMapping.rules.${t || 'default'}`;

const ENVIRONMENTS = PICKLISTS.environment;

// ---------------------------------------------------------------------------
// Categories. "Where it applies" is separate, because the same token can be a
// site code in a cluster name and a function code in a host name.
// ---------------------------------------------------------------------------
const CATEGORIES = [
  { key: 'epic', label: 'Epic component', color: '#6A1B9A', general: true },
  { key: 'environment', label: 'Environment', color: BLUE, general: true },
  { key: 'site', label: 'Site', color: '#00695C', general: false },
  { key: 'function', label: 'Function', color: HEAD, general: true },
  { key: 'vendor', label: 'Vendor', color: '#EF6C00', general: true },
  { key: 'decomm', label: 'Decommission marker', color: RED, general: true },
];
const categoryOf = (k) => CATEGORIES.find((c) => c.key === k) || { key: k, label: k, color: GREY, general: false };

const SCOPES = [
  { key: 'name', label: 'Server / DNS name' },
  { key: 'cluster', label: 'Cluster' },
  { key: 'datacenter', label: 'Datacenter' },
];

// ---------------------------------------------------------------------------
// The universal Epic component vocabulary. SUGGESTIONS ONLY — every one of these
// has to be confirmed by a person against this client's estate before it enters
// the dictionary. Epic ships these component names, but a client is perfectly
// free to have reused a token for something local, and several of them (BLOB,
// PROXY, FWARD) are also ordinary infrastructure words.
// ---------------------------------------------------------------------------
const EPIC_VOCAB = [
  { token: 'hsw', meaning: 'Hyperspace Web', category: 'epic' },
  { token: 'hsweb', meaning: 'Hyperspace Web', category: 'epic' },
  { token: 'eps', meaning: 'Epic Print Service', category: 'epic' },
  { token: 'intcon', meaning: 'Interconnect', category: 'epic' },
  { token: 'kuiper', meaning: 'Kuiper (Epic analytics / Caboodle infrastructure)', category: 'epic' },
  { token: 'mychart', meaning: 'MyChart (patient portal)', category: 'epic' },
  { token: 'blob', meaning: 'Blob storage server (Epic BLOB)', category: 'epic' },
  { token: 'bca', meaning: 'Business Continuity Access', category: 'epic' },
  { token: 'fward', meaning: 'Forward / forwarding server', category: 'epic' },
  { token: 'proxy', meaning: 'Proxy server', category: 'epic' },
  { token: 'odb', meaning: 'Operational Database', category: 'epic' },
  { token: 'clarity', meaning: 'Clarity reporting database', category: 'epic' },
  { token: 'caboodle', meaning: 'Caboodle enterprise data warehouse', category: 'epic' },
  { token: 'cogito', meaning: 'Cogito reporting suite', category: 'epic' },
  { token: 'epic', meaning: 'Epic (application family prefix)', category: 'epic' },
  { token: 'atrain', meaning: 'Training environment (Epic ATRAIN)', category: 'epic' },
  { token: 'citrix', meaning: 'Citrix presentation / EUC layer', category: 'vendor' },
];

// Detection vocabulary local to this screen. ServerMapping.js owns decommission
// FLAGGING on the mapping side; here these words only shape which patterns get
// surfaced and which questions get asked. Nothing is ever retired from here.
const DECOMM_WORDS = ['old', 'retire', 'retired', 'decom', 'decomm', 'decommission', 'decommissioned',
  'deprecated', 'obsolete', 'unused', 'dnu', 'donotuse', 'delete', 'deleted', 'placeholder', 'spare', 'dead'];

// Unambiguous environment words — deliberately >= 3 characters. Single letters
// ("p", "t", "d") are real conventions and also the fastest way to file a
// reporting box under Production, so they are excluded from every claim this
// screen makes about what the data proves.
const ENV_WORDS = ['prd', 'prod', 'production', 'tst', 'test', 'dev', 'devl', 'trn', 'train', 'trng',
  'stg', 'stage', 'staging', 'preprod', 'drp', 'drsite', 'ire', 'irz', 'isolated', 'val', 'valid',
  'uat', 'rpt', 'report', 'rptg', 'sbx', 'sandbox', 'poc'];
const ENV_WORD_SET = new Set(ENV_WORDS);

const normKey = (s) => String(s || '').trim().toLowerCase();
const stamp = () => new Date().toISOString().slice(0, 10);
const nowIso = () => new Date().toISOString();
const pctOf = (n, d) => (d ? Math.round((n / d) * 100) : 0);

let seq = 0;
const newId = (p = 'nd') => `${p}-${Date.now().toString(36)}-${(seq++).toString(36)}`;

const blankEntry = () => ({
  id: '',
  token: '',
  meaning: '',
  category: 'epic',
  scopes: ['name'],
  environment: '',
  looseToken: false,
  general: false,
  note: '',
  source: 'manual',
});

// ---------------------------------------------------------------------------
// The cross-tenant library. Only the universal part of the vocabulary — an Epic
// component name means the same thing at every health system. Site codes are
// refused at the door, in both directions, because ART/MDC/USB are facts about
// one client's real estate.
// ---------------------------------------------------------------------------
function loadGeneral() {
  try {
    const raw = localStorage.getItem(LS_GENERAL);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((x) => x && x.token && categoryOf(x.category).general) : [];
  } catch (e) { return []; }
}

function saveGeneral(list) {
  try { localStorage.setItem(LS_GENERAL, JSON.stringify(list)); } catch (e) { /* quota */ }
}

function upsertGeneral(entry) {
  if (!categoryOf(entry.category).general) return loadGeneral(); // site codes never leave the tenant
  const list = loadGeneral();
  const i = list.findIndex((x) => normKey(x.token) === normKey(entry.token));
  const rec = {
    token: normKey(entry.token),
    meaning: entry.meaning,
    category: entry.category,
    scopes: entry.scopes,
    updatedAt: nowIso(),
  };
  if (i >= 0) list[i] = rec; else list.push(rec);
  saveGeneral(list);
  return list;
}

function copyText(text, onDone) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => onDone(true), () => onDone(false));
      return;
    }
  } catch (e) { /* fall through */ }
  onDone(false);
}

// ---------------------------------------------------------------------------
// Small presentational bits
// ---------------------------------------------------------------------------
function Kpi({ label, value, sub, tone }) {
  const color = tone === 'bad' ? RED : tone === 'warn' ? AMBER : tone === 'good' ? GREEN : HEAD;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160, flex: '1 1 160px' }}>
      <Typography variant="caption" sx={{ color: GREY, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</Typography>
      {sub ? <Typography variant="caption" sx={{ color: GREY }}>{sub}</Typography> : null}
    </Paper>
  );
}

function UnknownChip() {
  return (
    <Chip size="small" label="meaning unknown" variant="outlined"
      sx={{ borderColor: AMBER, color: AMBER, fontWeight: 700 }} />
  );
}

function CategoryChip({ category }) {
  const c = categoryOf(category);
  return <Chip size="small" label={c.label} variant="outlined" sx={{ borderColor: c.color, color: c.color, fontWeight: 700 }} />;
}

// ===========================================================================
export default function NamingDictionary({ tenantId }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState(0);

  const [servers, setServers] = useState([]);
  const [apps, setApps] = useState([]);

  // Per-tenant persisted state: the confirmed dictionary, the consultant's
  // question list, and which auto-generated questions were dismissed.
  const [entries, setEntries] = useState([]);
  const [manualQuestions, setManualQuestions] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [general, setGeneral] = useState([]);

  const [draft, setDraft] = useState(blankEntry);
  const [editingId, setEditingId] = useState(null);
  const [patternKind, setPatternKind] = useState('prefix');
  const [newQuestion, setNewQuestion] = useState('');
  const [ruleApp, setRuleApp] = useState(null);
  const [copied, setCopied] = useState('');
  const [showAllUnexplained, setShowAllUnexplained] = useState(false);

  // ---- load ---------------------------------------------------------------
  const reload = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [c, a] = await Promise.all([
        store.listFactSheets(tenantId, { type: 'ITComponent' }),
        store.listFactSheets(tenantId, { type: 'Application' }),
      ]);
      setServers(c.filter(isServerLike));
      setApps(a);
    } catch (e) {
      setErr((e && e.message) || 'Could not load this workspace.');
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    let parsed = null;
    try {
      const raw = localStorage.getItem(LS_DICT(tenantId));
      parsed = raw ? JSON.parse(raw) : null;
    } catch (e) { parsed = null; }
    setEntries((parsed && Array.isArray(parsed.entries)) ? parsed.entries : []);
    setManualQuestions((parsed && Array.isArray(parsed.questions)) ? parsed.questions : []);
    setDismissed((parsed && Array.isArray(parsed.dismissed)) ? parsed.dismissed : []);
    setGeneral(loadGeneral());
    setDraft(blankEntry());
    setEditingId(null);
    setRuleApp(null);
  }, [tenantId]);

  const persist = useCallback((next) => {
    const body = {
      version: 1,
      tenantId,
      updatedAt: nowIso(),
      entries: next.entries !== undefined ? next.entries : entries,
      questions: next.questions !== undefined ? next.questions : manualQuestions,
      dismissed: next.dismissed !== undefined ? next.dismissed : dismissed,
    };
    if (next.entries !== undefined) setEntries(next.entries);
    if (next.questions !== undefined) setManualQuestions(next.questions);
    if (next.dismissed !== undefined) setDismissed(next.dismissed);
    try { localStorage.setItem(LS_DICT(tenantId), JSON.stringify(body)); } catch (e) { /* quota */ }
  }, [tenantId, entries, manualQuestions, dismissed]);

  // ---- the index: built ONCE per server load, never during render ----------
  const index = useMemo(() => servers.map((s) => {
    const f = s.fields || {};
    const name = String(s.name || f.name || '');
    const dnsName = String(f.dnsName || '');
    const cluster = String(f.cluster || '');
    const datacenter = String(f.datacenter || '');
    // "name" for dictionary purposes covers the host name AND the DNS name —
    // they carry the same vocabulary and a client who renames one rarely
    // renames both.
    const nameTok = [...new Set([...nameTokens(name), ...nameTokens(dnsName)])];
    const clusterTok = nameTokens(cluster);
    const dcTok = nameTokens(datacenter);
    return {
      id: s.id,
      s,
      name,
      dnsName,
      cluster,
      datacenter,
      lower: name.toLowerCase(),
      tok: { name: nameTok, cluster: clusterTok, datacenter: dcTok },
    };
  }), [servers]);

  // =========================================================================
  // TAB 1 analysis — what the names actually contain, with no interpretation
  // =========================================================================
  const analysis = useMemo(() => {
    const prefix = new Map();
    const suffix = new Map();
    const tokens = new Map();
    const infra = new Map();
    const blocks = new Map();
    let blockTotal = 0;
    let blockWithEnvWord = 0;
    let withEnvWord = 0;
    let decommCount = 0;
    let withCluster = 0;
    let withDc = 0;

    const bump = (map, key, sample) => {
      let r = map.get(key);
      if (!r) { r = { key, count: 0, samples: [] }; map.set(key, r); }
      r.count += 1;
      if (r.samples.length < EXAMPLES) r.samples.push(sample);
      return r;
    };

    for (let i = 0; i < index.length; i += 1) {
      const e = index[i];
      const lower = e.lower;
      if (e.cluster) withCluster += 1;
      if (e.datacenter) withDc += 1;

      // ---- leading alphabetic run: "epicart01" -> "epicart" ---------------
      const lead = (lower.match(/^[a-z]+/) || [''])[0];
      if (lead.length >= 2) bump(prefix, lead, e.name);

      // ---- trailing alphabetic run, ignoring a trailing number ------------
      const tail = (lower.replace(/[^a-z0-9]+$/, '').match(/([a-z]+)[0-9]*$/) || [])[1] || '';
      if (tail.length >= 2 && tail !== lead) bump(suffix, tail, e.name);

      // ---- every delimited token in the name / DNS name -------------------
      const nameEnv = [];
      for (let j = 0; j < e.tok.name.length; j += 1) {
        const t = e.tok.name[j];
        if (t.length < 2 || /^[0-9]+$/.test(t)) continue;
        bump(tokens, t, e.name);
        if (ENV_WORD_SET.has(t)) nameEnv.push(t);
      }
      if (nameEnv.length) withEnvWord += 1;
      if (e.tok.name.some((t) => DECOMM_WORDS.indexOf(t) >= 0)
        || lower.indexOf('placeholder') >= 0 || lower.indexOf('retired') >= 0) decommCount += 1;

      // ---- tokens carried by the infrastructure grouping ------------------
      const infraSeen = new Set();
      for (let j = 0; j < e.tok.cluster.length; j += 1) infraSeen.add(e.tok.cluster[j]);
      for (let j = 0; j < e.tok.datacenter.length; j += 1) infraSeen.add(e.tok.datacenter[j]);
      infraSeen.forEach((t) => {
        if (t.length < 2 || /^[0-9]+$/.test(t)) return;
        bump(infra, t, e.cluster || e.datacenter);
      });

      // ---- trailing three-digit block -------------------------------------
      // Detected, NOT decoded. We also measure the one thing that could decode
      // it — whether any of these servers also carries an unambiguous
      // environment word — and report that measurement honestly.
      const block = (lower.match(/([0-9]{3})$/) || [])[1];
      if (block) {
        blockTotal += 1;
        const r = bump(blocks, `${block[0]}xx`, e.name);
        r.withEnvWord = (r.withEnvWord || 0) + (nameEnv.length ? 1 : 0);
        if (nameEnv.length) blockWithEnvWord += 1;
      }
    }

    const rank = (map, min) => [...map.values()]
      .filter((r) => r.count >= min)
      .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));

    // A pattern seen once explains nothing; the floor scales with the estate so
    // a 40-server tenant still sees its own conventions.
    const floor = Math.max(2, Math.round(index.length / 500));

    return {
      total: index.length,
      prefixes: rank(prefix, floor),
      suffixes: rank(suffix, floor),
      tokens: rank(tokens, floor),
      infra: rank(infra, floor),
      blocks: [...blocks.values()].sort((a, b) => b.count - a.count || a.key.localeCompare(b.key)),
      blockTotal,
      blockWithEnvWord,
      withEnvWord,
      decommCount,
      withCluster,
      withDc,
      floor,
    };
  }, [index]);

  const patternList = useMemo(() => {
    if (patternKind === 'prefix') return analysis.prefixes;
    if (patternKind === 'suffix') return analysis.suffixes;
    if (patternKind === 'infra') return analysis.infra;
    return analysis.tokens;
  }, [patternKind, analysis]);

  const entryByToken = useMemo(() => {
    const m = new Map();
    entries.forEach((x) => m.set(normKey(x.token), x));
    return m;
  }, [entries]);

  // =========================================================================
  // Questions for the client — generated from what we could NOT decode
  // =========================================================================
  const autoQuestions = useMemo(() => {
    const out = [];

    if (analysis.blockTotal) {
      const series = analysis.blocks.map((b) => `${b.key[0]}00`).join(' / ');
      out.push({
        id: 'q-numeric-blocks',
        question: `What do the ${series} number series mean in your server names?`,
        why: `${analysis.blockTotal.toLocaleString()} servers end in a three-digit block `
          + `(${analysis.blocks.map((b) => `${b.key} ${b.count.toLocaleString()}`).join(', ')}). `
          + `${analysis.blockWithEnvWord.toLocaleString()} of them also carry an unambiguous environment word, so the `
          + 'meaning of the leading digit cannot be derived from the data — only you can tell us whether it is '
          + 'environment, pod, site, build generation or something else.',
        kind: 'undecodable',
      });
    }

    // Top undecoded name tokens — the ones that would explain the most records.
    analysis.tokens
      .filter((t) => !entryByToken.has(t.key) && !ENV_WORD_SET.has(t.key))
      .slice(0, 10)
      .forEach((t) => out.push({
        id: `q-token-${t.key}`,
        question: `What does "${t.key.toUpperCase()}" mean in your server names?`,
        why: `Seen in ${t.count.toLocaleString()} server names (${pctOf(t.count, analysis.total)}% of the estate). `
          + `Examples: ${t.samples.slice(0, 3).join(', ')}.`,
        kind: 'token',
      }));

    // Cluster / datacenter tokens carry the workload on most real estates, so an
    // undecoded one there is worth as much as an undecoded hostname token.
    analysis.infra
      .filter((t) => !entryByToken.has(t.key))
      .slice(0, 6)
      .forEach((t) => out.push({
        id: `q-infra-${t.key}`,
        question: `In your cluster / datacenter naming, what does "${t.key.toUpperCase()}" refer to?`,
        why: `Appears in the cluster or datacenter of ${t.count.toLocaleString()} servers `
          + `(${pctOf(t.count, analysis.total)}%). Examples: ${t.samples.slice(0, 3).join(', ')}.`,
        kind: 'infra',
      }));

    if (analysis.decommCount) {
      out.push({
        id: 'q-decomm',
        question: `Are the ${analysis.decommCount.toLocaleString()} servers whose names suggest retirement actually decommissioned?`,
        why: 'Names containing words such as "-old", "retired", "placeholder" or "spare". '
          + 'Flagged only — nothing has been retired, and a name is not evidence.',
        kind: 'decomm',
      });
    }

    if (analysis.total && analysis.withEnvWord / analysis.total < 0.5) {
      out.push({
        id: 'q-env-convention',
        question: 'How do we tell production from non-production for the servers whose names say nothing?',
        why: `Only ${analysis.withEnvWord.toLocaleString()} of ${analysis.total.toLocaleString()} servers `
          + `(${pctOf(analysis.withEnvWord, analysis.total)}%) carry an unambiguous environment word in the name. `
          + `Cluster is populated on ${analysis.withCluster.toLocaleString()} and datacenter on `
          + `${analysis.withDc.toLocaleString()} — is the environment encoded there, in a CMDB field, or only in people's heads?`,
        kind: 'coverage',
      });
    }

    return out.filter((q) => dismissed.indexOf(q.id) < 0);
  }, [analysis, entryByToken, dismissed]);

  const allQuestions = useMemo(
    () => [...autoQuestions, ...manualQuestions],
    [autoQuestions, manualQuestions]
  );

  // =========================================================================
  // TAB 2 — suggestions (never auto-applied) and the dictionary itself
  // =========================================================================
  // Counts are live against THIS estate, so a suggestion that means nothing here
  // is visibly worth nothing here.
  const tokenCount = useMemo(() => {
    const m = new Map();
    analysis.tokens.forEach((t) => m.set(t.key, t.count));
    return m;
  }, [analysis]);

  const infraCount = useMemo(() => {
    const m = new Map();
    analysis.infra.forEach((t) => m.set(t.key, t.count));
    return m;
  }, [analysis]);

  const suggestions = useMemo(() => {
    const seen = new Set();
    const list = [];
    EPIC_VOCAB.forEach((v) => {
      seen.add(v.token);
      list.push({ ...v, origin: 'Epic vocabulary' });
    });
    general.forEach((g) => {
      if (seen.has(normKey(g.token))) return;
      seen.add(normKey(g.token));
      list.push({ token: normKey(g.token), meaning: g.meaning, category: g.category, origin: 'General library' });
    });
    return list
      .filter((v) => !entryByToken.has(v.token))
      .map((v) => {
        // A prefix family such as "epichsweb" contains the token but never
        // tokenises to it, so count both ways and say which one matched.
        const exact = tokenCount.get(v.token) || 0;
        const infraHits = infraCount.get(v.token) || 0;
        let embedded = 0;
        if (!exact) {
          for (let i = 0; i < analysis.prefixes.length; i += 1) {
            const p = analysis.prefixes[i];
            if (p.key !== v.token && p.key.indexOf(v.token) >= 0) embedded += p.count;
          }
        }
        return { ...v, exact, embedded, infraHits, total: exact + embedded + infraHits };
      })
      .sort((a, b) => b.total - a.total || a.token.localeCompare(b.token));
  }, [general, entryByToken, tokenCount, infraCount, analysis]);

  // =========================================================================
  // TAB 3 — how much of the estate the CONFIRMED dictionary explains
  // =========================================================================
  const coverage = useMemo(() => {
    const perEntry = new Map(entries.map((x) => [x.id, 0]));
    const perCategory = new Map();
    const explainedIds = new Set();
    const unexplained = [];

    for (let i = 0; i < index.length; i += 1) {
      const e = index[i];
      let hit = false;
      for (let j = 0; j < entries.length; j += 1) {
        const en = entries[j];
        const token = normKey(en.token);
        if (!token) continue;
        const scopes = en.scopes && en.scopes.length ? en.scopes : ['name'];
        let matched = false;
        for (let k = 0; k < scopes.length; k += 1) {
          if (hasToken(e.tok[scopes[k]] || [], token, !!en.looseToken)) { matched = true; break; }
        }
        if (!matched) continue;
        perEntry.set(en.id, (perEntry.get(en.id) || 0) + 1);
        perCategory.set(en.category, (perCategory.get(en.category) || 0) + 1);
        hit = true;
      }
      if (hit) explainedIds.add(e.id); else unexplained.push(e);
    }

    return {
      total: index.length,
      explained: explainedIds.size,
      unexplained,
      perEntry,
      perCategory: [...perCategory.entries()]
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count),
    };
  }, [index, entries]);

  // ---- rule definitions, in ServerMapping.js's own shape -------------------
  // One rule per (entry, scope), except an entry that applies everywhere, which
  // collapses to ServerMapping's "any" field. appId is only filled when the user
  // explicitly picks a target application: guessing which application a token
  // belongs to is the mapper's job and a person's decision.
  const generatedRules = useMemo(() => {
    const out = [];
    entries.forEach((en) => {
      const token = normKey(en.token);
      if (!token) return;
      const scopes = en.scopes && en.scopes.length ? en.scopes : ['name'];
      const fields = scopes.length === SCOPES.length ? ['any'] : scopes.slice();
      const hits = coverage.perEntry.get(en.id) || 0;
      fields.forEach((field) => {
        out.push({
          rule: {
            id: `rule-nd-${en.id}-${field}`,
            enabled: true,
            field,
            mode: 'token',
            pattern: token,
            looseToken: !!en.looseToken,
            appId: ruleApp ? ruleApp.id : '',
            appName: ruleApp ? ruleApp.name : '',
            // Only an entry the client called an environment can set one. Every
            // other token names a component, a site or a function — it says
            // nothing about prod vs test, so it lands as a named "Other" and a
            // person picks the real environment in Server Mapping.
            environment: en.category === 'environment' && en.environment ? en.environment : 'Other',
            environmentLabel: en.category === 'environment' && en.environment ? '' : en.meaning,
            template: '{app} {envShort}',
            confidence: token.length <= 2 ? 0.55 : 0.85,
          },
          meaning: en.meaning,
          category: en.category,
          hits,
          needsApp: !ruleApp,
          needsEnv: !(en.category === 'environment' && en.environment),
        });
      });
    });
    return out.sort((a, b) => b.hits - a.hits || a.rule.pattern.localeCompare(b.rule.pattern));
  }, [entries, coverage, ruleApp]);

  const rulesJson = useMemo(
    () => JSON.stringify(generatedRules.map((r) => r.rule), null, 2),
    [generatedRules]
  );

  // =========================================================================
  // Actions
  // =========================================================================
  const startEntryFrom = (token, scope, category, meaning) => {
    setDraft({
      ...blankEntry(),
      token: normKey(token),
      meaning: meaning || '',
      category: category || 'epic',
      scopes: [scope || 'name'],
      source: meaning ? 'suggested' : 'manual',
    });
    setEditingId(null);
    setTab(1);
  };

  const saveDraft = () => {
    const token = normKey(draft.token);
    if (!token || !draft.meaning.trim()) return;
    const rec = {
      ...draft,
      token,
      meaning: draft.meaning.trim(),
      // A site code is a client fact. It stays in this workspace whatever the
      // switch was left on.
      general: categoryOf(draft.category).general ? !!draft.general : false,
      confirmedAt: nowIso(),
    };
    let next;
    if (editingId) {
      next = entries.map((x) => (x.id === editingId ? { ...rec, id: editingId } : x));
    } else {
      const dupe = entries.find((x) => normKey(x.token) === token);
      rec.id = dupe ? dupe.id : newId('tok');
      next = dupe ? entries.map((x) => (x.id === dupe.id ? rec : x)) : [...entries, rec];
    }
    persist({ entries: next });
    if (rec.general) setGeneral(upsertGeneral(rec));
    setDraft(blankEntry());
    setEditingId(null);
  };

  const deleteEntry = (id) => persist({ entries: entries.filter((x) => x.id !== id) });

  const dismissQuestion = (id) => persist({ dismissed: [...dismissed, id] });

  const addManualQuestion = () => {
    const q = newQuestion.trim();
    if (!q) return;
    persist({ questions: [...manualQuestions, { id: newId('q'), question: q, why: 'Added by the consultant.', kind: 'manual' }] });
    setNewQuestion('');
  };

  const askAboutPattern = (row, kind) => {
    const label = row.key.toUpperCase();
    const q = kind === 'infra'
      ? `In your cluster / datacenter naming, what does "${label}" refer to?`
      : `What does "${label}" mean in your server names?`;
    if (allQuestions.some((x) => x.question === q)) return;
    persist({
      questions: [...manualQuestions, {
        id: newId('q'),
        question: q,
        why: `Seen ${row.count.toLocaleString()} times (${pctOf(row.count, analysis.total)}% of the estate). `
          + `Examples: ${row.samples.slice(0, 3).join(', ')}.`,
        kind: 'manual',
      }],
    });
  };

  const exportQuestions = () => exportCsv(
    `primordia-naming-questions-${normKey(tenantId) || 'tenant'}-${stamp()}.csv`,
    allQuestions,
    [
      { label: 'Question for the client', value: 'question' },
      { label: 'What we measured', value: 'why' },
      { label: 'Origin', value: (q) => (q.kind === 'manual' ? 'added by consultant' : `detected (${q.kind})`) },
      { label: 'Answer', value: () => '' },
    ]
  );

  const exportDictionary = () => exportCsv(
    `primordia-naming-dictionary-${normKey(tenantId) || 'tenant'}-${stamp()}.csv`,
    entries,
    [
      { label: 'Token', value: (x) => x.token },
      { label: 'Meaning', value: (x) => x.meaning },
      { label: 'Category', value: (x) => categoryOf(x.category).label },
      { label: 'Applies to', value: (x) => (x.scopes || []).map((s) => (SCOPES.find((y) => y.key === s) || {}).label || s).join(' + ') },
      { label: 'Environment', value: (x) => x.environment || '' },
      { label: 'General (cross-client)', value: (x) => (x.general ? 'yes' : 'no') },
      { label: 'Servers explained', value: (x) => coverage.perEntry.get(x.id) || 0 },
      { label: 'Confirmed at', value: (x) => x.confirmedAt || '' },
    ]
  );

  const exportUnexplained = () => exportCsv(
    `primordia-naming-unexplained-${normKey(tenantId) || 'tenant'}-${stamp()}.csv`,
    coverage.unexplained,
    [
      { label: 'Server', value: (e) => e.name },
      { label: 'DNS name', value: (e) => e.dnsName },
      { label: 'Cluster', value: (e) => e.cluster },
      { label: 'Datacenter', value: (e) => e.datacenter },
      { label: 'Fact sheet id', value: (e) => e.id },
    ]
  );

  const exportRulesJson = () => {
    const blob = new Blob([rulesJson], { type: 'application/json' });
    triggerDownload(blob, `primordia-naming-rules-${normKey(tenantId) || 'tenant'}-${stamp()}.json`);
  };

  const doCopy = (text, label) => copyText(text, (ok) => setCopied(ok ? label : 'Copy failed — select the text and copy manually.'));

  // =========================================================================
  // Render
  // =========================================================================
  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }} spacing={2}>
        <CircularProgress />
        <Typography variant="body2" sx={{ color: GREY }}>Reading the estate&apos;s names…</Typography>
      </Stack>
    );
  }

  const draftValid = !!normKey(draft.token) && !!draft.meaning.trim();
  const unexplainedRows = showAllUnexplained
    ? coverage.unexplained.slice(0, 1000)
    : coverage.unexplained.slice(0, SAMPLE_ROWS);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 1, gap: 1 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: HEAD }}>Naming dictionary</Typography>
            <HelpBubble topic="namingDictionary" />
          </Stack>
          <Typography variant="body2" sx={{ color: GREY }}>
            {analysis.total.toLocaleString()} servers analysed · {entries.length} confirmed token
            {entries.length === 1 ? '' : 's'} · {allQuestions.length} open question
            {allQuestions.length === 1 ? '' : 's'} for the client
          </Typography>
        </Box>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {copied && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setCopied('')}>{copied}</Alert>}

      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>A name is a clue, not a fact</AlertTitle>
        Only <b>{analysis.withEnvWord.toLocaleString()} of {analysis.total.toLocaleString()}</b>{' '}
        ({pctOf(analysis.withEnvWord, analysis.total)}%) servers here carry an unambiguous environment word in the
        name, while cluster is populated on <b>{analysis.withCluster.toLocaleString()}</b> and datacenter on{' '}
        <b>{analysis.withDc.toLocaleString()}</b>. Decoding names <b>supplements</b> the cluster and datacenter
        signal — it will never replace it. Nothing on this screen writes to a fact sheet: mapping happens in{' '}
        <b>Server mapping</b>, where the preview / apply / revert workflow lives.
      </Alert>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label={<TabLabel text={`Detected patterns${patternList.length ? ` (${patternList.length})` : ''}`} topic="tabDetectedPatterns" />} />
        <Tab label={<TabLabel text={`Dictionary (${entries.length})`} topic="tabDictionary" />} />
        <Tab label={<TabLabel text="Apply" topic="tabApplyNaming" />} />
      </Tabs>

      {/* ==================================================================== */}
      {/* TAB 1 — DETECTED PATTERNS                                            */}
      {/* ==================================================================== */}
      {tab === 0 && (
        <Stack spacing={2}>
          <Alert severity="info">
            <AlertTitle>Everything below is a pattern, not a meaning</AlertTitle>
            These are the shapes that recur in this tenant&apos;s own names, ranked by how many records each one
            explains. Not one of them has a meaning until somebody at the client supplies it — so each row reads
            &quot;seen N times, meaning unknown&quot; until you record what it means on the Dictionary tab. Patterns
            below the noise floor ({analysis.floor}+ occurrences) are hidden.
          </Alert>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Kpi label="Servers analysed" value={analysis.total.toLocaleString()} />
            <Kpi label="Recurring prefixes" value={analysis.prefixes.length.toLocaleString()} sub="leading letter runs" />
            <Kpi label="Distinct name tokens" value={analysis.tokens.length.toLocaleString()} sub={`seen ${analysis.floor}+ times`} />
            <Kpi label="Cluster / datacenter tokens" value={analysis.infra.length.toLocaleString()} sub="usually the stronger signal" />
            <Kpi label="Names with an environment word" value={analysis.withEnvWord.toLocaleString()}
              sub={`${pctOf(analysis.withEnvWord, analysis.total)}% — the rest say nothing`}
              tone={analysis.withEnvWord < analysis.total / 2 ? 'bad' : undefined} />
            <Kpi label="Names suggesting retirement" value={analysis.decommCount.toLocaleString()}
              sub="flagged only, never retired" tone={analysis.decommCount ? 'warn' : undefined} />
          </Stack>

          {/* ---- the undecodable pattern, called out on its own ----------- */}
          {analysis.blockTotal > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderColor: AMBER, borderWidth: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <HelpOutlineIcon sx={{ color: AMBER }} />
                <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                  Detected but NOT decodable — the numeric series
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: GREY, mb: 1 }}>
                {analysis.blockTotal.toLocaleString()} servers end in a three-digit block. The leading digit is
                obviously significant — but <b>{analysis.blockWithEnvWord.toLocaleString()}</b> of those servers also
                carry an unambiguous environment word, so <b>nothing in this data can tell us what it means</b>. It
                could be environment, pod, site, hardware generation or an internal build number. We do not guess:
                this becomes a question for the client.
              </Typography>
              <Table size="small">
                <TableHead><TableRow sx={headSx}>
                  <TableCell>Series</TableCell><TableCell align="right">Servers</TableCell>
                  <TableCell align="right">Also carry an environment word</TableCell>
                  <TableCell>Examples</TableCell><TableCell>Meaning</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {analysis.blocks.map((b) => (
                    <TableRow key={b.key} hover>
                      <TableCell sx={{ fontWeight: 700 }}><code>{b.key}</code></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{b.count.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: (b.withEnvWord || 0) ? HEAD : RED, fontWeight: 700 }}>
                        {(b.withEnvWord || 0).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ color: GREY, fontSize: 12 }}>{b.samples.slice(0, 3).join(', ')}</TableCell>
                      <TableCell><UnknownChip /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* ---- ranked patterns ----------------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                Recurring patterns — ranked by how many records each explains
              </Typography>
              <TextField select size="small" value={patternKind} onChange={(e) => setPatternKind(e.target.value)} sx={{ minWidth: 260 }}>
                <MenuItem value="prefix">Prefixes (leading letters of the name)</MenuItem>
                <MenuItem value="suffix">Suffixes (trailing letters before the number)</MenuItem>
                <MenuItem value="token">Delimited tokens in name / DNS</MenuItem>
                <MenuItem value="infra">Tokens in cluster / datacenter</MenuItem>
              </TextField>
            </Stack>
            {!patternList.length ? (
              <Typography variant="body2" sx={{ color: GREY }}>
                Nothing recurs {analysis.floor} or more times in this view.
              </Typography>
            ) : (
              <>
                <Table size="small">
                  <TableHead><TableRow sx={headSx}>
                    <TableCell>Pattern</TableCell><TableCell align="right">Seen</TableCell>
                    <TableCell align="right">% of estate</TableCell><TableCell>Examples</TableCell>
                    <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {patternList.slice(0, PATTERN_CAP).map((row) => {
                      const known = entryByToken.get(row.key);
                      return (
                        <TableRow key={row.key} hover>
                          <TableCell sx={{ fontWeight: 700 }}><code>{row.key}</code></TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{row.count.toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ color: GREY }}>{pctOf(row.count, analysis.total)}%</TableCell>
                          <TableCell sx={{ color: GREY, fontSize: 12, maxWidth: 320, wordBreak: 'break-all' }}>
                            {row.samples.join(', ')}
                          </TableCell>
                          <TableCell>
                            {known ? (
                              <Tooltip title={`Recorded as: ${known.meaning}`}>
                                <Chip size="small" label={known.meaning} variant="outlined"
                                  sx={{ borderColor: GREEN, color: GREEN, fontWeight: 700, maxWidth: 220 }} />
                              </Tooltip>
                            ) : <UnknownChip />}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Record what this means (Dictionary tab)">
                              <IconButton size="small"
                                onClick={() => startEntryFrom(row.key, patternKind === 'infra' ? 'cluster' : 'name',
                                  patternKind === 'infra' ? 'site' : 'epic', '')}>
                                <AddIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Add this to the question list for the client">
                              <IconButton size="small" onClick={() => askAboutPattern(row, patternKind)}>
                                <HelpOutlineIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {patternList.length > PATTERN_CAP && (
                  <Typography variant="caption" sx={{ color: GREY }}>
                    Showing the top {PATTERN_CAP} of {patternList.length.toLocaleString()} — the rest have smaller counts
                    and explain proportionally less.
                  </Typography>
                )}
              </>
            )}
          </Paper>

          {/* ---- questions for the client -------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ gap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                Questions for the client — {allQuestions.length}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<ContentCopyIcon />}
                  onClick={() => doCopy(allQuestions.map((q, i) => `${i + 1}. ${q.question}\n   (${q.why})`).join('\n\n'), 'Question list copied to the clipboard.')}>
                  Copy list
                </Button>
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!allQuestions.length}
                  onClick={exportQuestions} sx={{ borderColor: BLUE, color: BLUE }}>
                  Export questions to CSV
                </Button>
              </Stack>
            </Stack>
            <Typography variant="caption" sx={{ color: GREY }}>
              Generated from what we could <b>not</b> decode. This is the agenda for the next naming-conventions
              session — the CSV has an empty Answer column so it can go straight to the client.
            </Typography>

            <Table size="small" sx={{ mt: 1 }}>
              <TableHead><TableRow sx={headSx}>
                <TableCell>Question</TableCell><TableCell>What we measured</TableCell>
                <TableCell>Origin</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {allQuestions.map((q) => (
                  <TableRow key={q.id} hover>
                    <TableCell sx={{ fontWeight: 600, maxWidth: 340 }}>{q.question}</TableCell>
                    <TableCell sx={{ color: GREY, fontSize: 12, maxWidth: 460 }}>{q.why}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined"
                        label={q.kind === 'manual' ? 'added by you' : q.kind === 'undecodable' ? 'undecodable pattern' : 'detected'}
                        sx={{ borderColor: q.kind === 'undecodable' ? AMBER : GREY, color: q.kind === 'undecodable' ? AMBER : GREY }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove from the list">
                        <IconButton size="small"
                          onClick={() => (q.kind === 'manual'
                            ? persist({ questions: manualQuestions.filter((x) => x.id !== q.id) })
                            : dismissQuestion(q.id))}>
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!allQuestions.length && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: GREY }}>
                      No open questions. Either everything recurring has been decoded, or the list was cleared.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <TextField size="small" sx={{ flex: 1, minWidth: 280 }} label="Add your own question"
                value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addManualQuestion(); }} />
              <Button variant="outlined" startIcon={<AddIcon />} disabled={!newQuestion.trim()}
                onClick={addManualQuestion} sx={{ borderColor: BLUE, color: BLUE }}>
                Add
              </Button>
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* ==================================================================== */}
      {/* TAB 2 — DICTIONARY                                                   */}
      {/* ==================================================================== */}
      {tab === 1 && (
        <Stack spacing={2}>
          <Alert severity="info">
            <AlertTitle>This vocabulary belongs to this client</AlertTitle>
            A token means whatever <b>this</b> client says it means. Everything here is stored per workspace
            (<code>{LS_DICT(tenantId)}</code>). Epic component names are the one genuinely universal part, so an entry
            can be marked <b>general</b> and reused to seed the next engagement — site codes cannot, because ART, MDC
            and USB are facts about one client&apos;s real estate and must not leak between workspaces.
          </Alert>

          {/* ---- entry editor -------------------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>
              {editingId ? 'Edit entry' : 'New entry'}
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <TextField size="small" label="Token" value={draft.token} sx={{ minWidth: 160 }}
                onChange={(e) => setDraft({ ...draft, token: e.target.value })}
                helperText="matched at delimiter / camelCase / digit boundaries" />
              <TextField size="small" label="Meaning (what the client told you)" value={draft.meaning}
                sx={{ minWidth: 300, flex: 1 }}
                onChange={(e) => setDraft({ ...draft, meaning: e.target.value })}
                helperText="required — an entry without a meaning is just a pattern" />
              <TextField select size="small" label="Category" value={draft.category} sx={{ minWidth: 200 }}
                onChange={(e) => setDraft({
                  ...draft,
                  category: e.target.value,
                  general: categoryOf(e.target.value).general ? draft.general : false,
                })}>
                {CATEGORIES.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
              </TextField>
              {draft.category === 'environment' && (
                <TextField select size="small" label="Maps to environment" value={draft.environment} sx={{ minWidth: 220 }}
                  onChange={(e) => setDraft({ ...draft, environment: e.target.value })}
                  helperText="used when generating rules">
                  {ENVIRONMENTS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </TextField>
              )}
            </Stack>

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: GREY, fontWeight: 700 }}>Where it applies:</Typography>
              {SCOPES.map((s) => (
                <FormControlLabel key={s.key}
                  control={
                    <Checkbox size="small" checked={(draft.scopes || []).indexOf(s.key) >= 0}
                      onChange={(e) => {
                        const set = new Set(draft.scopes || []);
                        if (e.target.checked) set.add(s.key); else set.delete(s.key);
                        setDraft({ ...draft, scopes: [...set] });
                      }} />
                  }
                  label={<Typography variant="body2">{s.label}</Typography>} />
              ))}
              <FormControlLabel
                control={<Switch size="small" checked={!!draft.looseToken}
                  onChange={(e) => setDraft({ ...draft, looseToken: e.target.checked })} />}
                label={<Typography variant="caption" sx={{ color: GREY }}>
                  Also match a token that <b>ends with</b> this (catches undelimited names like <code>vmepichsw01</code>)
                </Typography>} />
            </Stack>

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
              <Tooltip title={categoryOf(draft.category).general
                ? 'Reusable when seeding a new workspace'
                : 'Site codes are client facts — they stay in this workspace'}>
                <FormControlLabel
                  control={<Switch size="small" checked={!!draft.general}
                    disabled={!categoryOf(draft.category).general}
                    onChange={(e) => setDraft({ ...draft, general: e.target.checked })} />}
                  label={<Typography variant="body2">
                    General — universal vocabulary, may seed other tenants
                  </Typography>} />
              </Tooltip>
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" startIcon={<AddIcon />} disabled={!draftValid} onClick={saveDraft}
                sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}>
                {editingId ? 'Save entry' : 'Add entry'}
              </Button>
              {editingId && <Button onClick={() => { setEditingId(null); setDraft(blankEntry()); }}>Cancel</Button>}
            </Stack>
          </Paper>

          {/* ---- suggestions --------------------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <LibraryBooksIcon sx={{ color: BLUE }} />
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                Suggestions to confirm — {suggestions.length} not yet in this dictionary
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: GREY }}>
              The well-known Epic component vocabulary, plus anything previously marked general on another engagement.
              <b> Nothing here is applied.</b> Counts are live against this estate — a suggestion with a count of zero
              probably does not exist here, and a suggestion with a big count still means whatever the client says it
              means. Confirm loads it into the editor above so you can correct the wording before it is recorded.
            </Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead><TableRow sx={headSx}>
                <TableCell>Token</TableCell><TableCell>Suggested meaning</TableCell><TableCell>Category</TableCell>
                <TableCell align="right">Exact token hits</TableCell><TableCell align="right">Inside a prefix</TableCell>
                <TableCell align="right">In cluster / DC</TableCell><TableCell>Source</TableCell>
                <TableCell align="right">Confirm</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {suggestions.map((v) => (
                  <TableRow key={v.token} hover sx={{ opacity: v.total ? 1 : 0.5 }}>
                    <TableCell sx={{ fontWeight: 700 }}><code>{v.token}</code></TableCell>
                    <TableCell>{v.meaning}</TableCell>
                    <TableCell><CategoryChip category={v.category} /></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: v.exact ? HEAD : GREY }}>{v.exact.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: GREY }}>
                      <Tooltip title="Servers whose leading letter-run contains this token but does not tokenise to it — e.g. epichsweb. Switch on the suffix option, or add a prefix entry instead.">
                        <span>{v.embedded.toLocaleString()}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ color: GREY }}>{v.infraHits.toLocaleString()}</TableCell>
                    <TableCell sx={{ color: GREY, fontSize: 12 }}>{v.origin}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => startEntryFrom(v.token, v.infraHits > v.exact ? 'cluster' : 'name', v.category, v.meaning)}>
                        Confirm…
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!suggestions.length && (
                  <TableRow><TableCell colSpan={8} sx={{ color: GREY }}>
                    Every suggested token has already been confirmed or replaced in this workspace.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* ---- the dictionary ------------------------------------------ */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                {tenantId}&apos;s vocabulary — {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!entries.length}
                onClick={exportDictionary} sx={{ borderColor: BLUE, color: BLUE }}>
                Export dictionary to CSV
              </Button>
            </Stack>
            {!entries.length ? (
              <Typography variant="body2" sx={{ color: GREY }}>
                Nothing confirmed yet. Until a person records a meaning, every pattern on the previous tab stays
                &quot;meaning unknown&quot; and the Apply tab explains 0 of {analysis.total.toLocaleString()} servers.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead><TableRow sx={headSx}>
                  <TableCell>Token</TableCell><TableCell>Meaning</TableCell><TableCell>Category</TableCell>
                  <TableCell>Applies to</TableCell><TableCell align="right">Servers explained</TableCell>
                  <TableCell>Scope</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {entries.map((x) => {
                    const hits = coverage.perEntry.get(x.id) || 0;
                    return (
                      <TableRow key={x.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <code>{x.token}</code>{x.looseToken ? <Typography variant="caption" sx={{ color: GREY }}> +suffix</Typography> : null}
                        </TableCell>
                        <TableCell>
                          {x.meaning}
                          {x.category === 'environment' && x.environment
                            ? <Typography variant="caption" sx={{ display: 'block', color: GREY }}>→ {x.environment}</Typography>
                            : null}
                        </TableCell>
                        <TableCell><CategoryChip category={x.category} /></TableCell>
                        <TableCell sx={{ color: GREY, fontSize: 12 }}>
                          {(x.scopes || []).map((s) => (SCOPES.find((y) => y.key === s) || {}).label || s).join(' + ')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: hits ? HEAD : RED }}>
                          {hits.toLocaleString()}
                          {!hits ? <Typography variant="caption" sx={{ display: 'block', color: RED }}>explains nothing here</Typography> : null}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={x.general ? 'general' : 'this client only'}
                            sx={{ borderColor: x.general ? BLUE : GREY, color: x.general ? BLUE : GREY }} />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => { setDraft({ ...blankEntry(), ...x }); setEditingId(x.id); }}>
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton size="small" onClick={() => deleteEntry(x.id)}>
                            <DeleteOutlineIcon fontSize="inherit" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Stack>
      )}

      {/* ==================================================================== */}
      {/* TAB 3 — APPLY                                                        */}
      {/* ==================================================================== */}
      {tab === 2 && (
        <Stack spacing={2}>
          {/* Leads with the remainder, deliberately. "62% explained" is the most
              flattering and least useful number this screen could show. */}
          <Alert severity={coverage.unexplained.length ? 'warning' : 'success'}>
            <AlertTitle>
              {coverage.unexplained.length.toLocaleString()} of {coverage.total.toLocaleString()} servers are still
              unexplained by this dictionary
            </AlertTitle>
            {coverage.explained.toLocaleString()} carry at least one decoded token
            ({pctOf(coverage.explained, coverage.total)}%). Decoding a name does not map a server — it narrows the
            question. The remainder is the work list, and it is exportable below.
          </Alert>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Kpi label="Unexplained" value={coverage.unexplained.length.toLocaleString()}
              sub="the work list" tone={coverage.unexplained.length ? 'bad' : 'good'} />
            <Kpi label="At least one decoded token" value={coverage.explained.toLocaleString()} />
            <Kpi label="Total servers" value={coverage.total.toLocaleString()} />
            <Kpi label="% explained" value={`${pctOf(coverage.explained, coverage.total)}%`} sub="not a quality measure" />
            <Kpi label="Confirmed tokens" value={entries.length.toLocaleString()} />
            <Kpi label="Rules generated" value={generatedRules.length.toLocaleString()} sub="for Server mapping" />
          </Stack>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1 }}>
              What each category explains
            </Typography>
            {!coverage.perCategory.length ? (
              <Typography variant="body2" sx={{ color: GREY }}>
                Nothing confirmed yet — the dictionary explains none of this estate.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead><TableRow sx={headSx}>
                  <TableCell>Category</TableCell><TableCell align="right">Token matches</TableCell>
                  <TableCell align="right">% of estate</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {coverage.perCategory.map((c) => (
                    <TableRow key={c.category} hover>
                      <TableCell><CategoryChip category={c.category} /></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{c.count.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: GREY }}>{pctOf(c.count, coverage.total)}%</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ color: RED, fontWeight: 700 }}>(no decoded token at all)</TableCell>
                    <TableCell align="right" sx={{ color: RED, fontWeight: 700 }}>{coverage.unexplained.length.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: RED, fontWeight: 700 }}>{pctOf(coverage.unexplained.length, coverage.total)}%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
            <Typography variant="caption" sx={{ color: GREY }}>
              A server can carry several tokens, so category counts overlap and will not sum to the explained total.
            </Typography>
          </Paper>

          {/* ---- rule handoff -------------------------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 0.5 }}>
              Rule definitions for Server mapping
            </Typography>
            <Typography variant="caption" sx={{ color: GREY }}>
              Generated in the exact shape <b>Server mapping</b> consumes — token mode, matched at delimiter /
              camelCase / digit boundaries, one rule per token and scope. This screen deliberately does not write
              them: mapping runs through Server mapping&apos;s preview / apply / revert workflow, where a person sees
              every proposed link before anything touches a fact sheet. Recreate them on the Rules tab there, or paste
              this JSON into <code>{LS_SERVER_RULES(tenantId)}</code>.
            </Typography>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 1.5, mb: 1 }}>
              <Autocomplete
                size="small" sx={{ minWidth: 320 }} options={apps} getOptionLabel={(o) => o.name || ''}
                value={ruleApp} onChange={(e, v) => setRuleApp(v)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Target application (optional)"
                  helperText="Server mapping ignores a rule with no application. Which app a token belongs to is a person's decision, so it is left blank unless you choose one." />}
              />
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={<ContentCopyIcon />} disabled={!generatedRules.length}
                onClick={() => doCopy(rulesJson, `${generatedRules.length} rule definition(s) copied to the clipboard.`)}>
                Copy JSON
              </Button>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!generatedRules.length}
                onClick={exportRulesJson} sx={{ borderColor: BLUE, color: BLUE }}>
                Export rules (.json)
              </Button>
            </Stack>

            {!generatedRules.length ? (
              <Typography variant="body2" sx={{ color: GREY }}>
                No confirmed tokens, so there is nothing to hand over yet.
              </Typography>
            ) : (
              <>
                <Table size="small">
                  <TableHead><TableRow sx={headSx}>
                    <TableCell>Pattern</TableCell><TableCell>Match field</TableCell><TableCell>Meaning</TableCell>
                    <TableCell>Environment</TableCell><TableCell align="right">Would match</TableCell>
                    <TableCell>Still needs a human</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {generatedRules.map((g) => (
                      <TableRow key={g.rule.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}><code>{g.rule.pattern}</code></TableCell>
                        <TableCell sx={{ color: GREY }}>{g.rule.field}</TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>{g.meaning}</TableCell>
                        <TableCell sx={{ color: GREY }}>
                          {g.rule.environment}
                          {g.rule.environmentLabel ? ` — ${g.rule.environmentLabel}` : ''}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{g.hits.toLocaleString()}</TableCell>
                        <TableCell sx={{ fontSize: 12, color: AMBER }}>
                          {[g.needsApp ? 'pick the application' : '', g.needsEnv ? 'pick the environment' : '']
                            .filter(Boolean).join(' · ') || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Divider sx={{ my: 1.5 }} />
                <TextField
                  multiline minRows={4} maxRows={14} fullWidth size="small" value={rulesJson}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }} />
              </>
            )}
          </Paper>

          {/* ---- the remainder, always on screen ------------------------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ gap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                UNEXPLAINED — {coverage.unexplained.length.toLocaleString()} server
                {coverage.unexplained.length === 1 ? '' : 's'} carrying no token this dictionary knows
              </Typography>
              <Stack direction="row" spacing={1}>
                {coverage.unexplained.length > SAMPLE_ROWS && (
                  <Button size="small" onClick={() => setShowAllUnexplained(!showAllUnexplained)}>
                    {showAllUnexplained ? `Show first ${SAMPLE_ROWS}` : 'Show more'}
                  </Button>
                )}
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
                  disabled={!coverage.unexplained.length} onClick={exportUnexplained}
                  sx={{ borderColor: BLUE, color: BLUE }}>
                  Export unexplained to CSV
                </Button>
              </Stack>
            </Stack>
            <Typography variant="caption" sx={{ color: GREY }}>
              Their cluster and datacenter are shown because that is where the answer usually is when the name says
              nothing. Showing {unexplainedRows.length.toLocaleString()} rows; the export covers all of them.
            </Typography>
            <Box sx={{ mt: 1, maxHeight: 520, overflow: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow sx={headSx}>
                  <TableCell>Server</TableCell><TableCell>DNS</TableCell>
                  <TableCell>Cluster</TableCell><TableCell>Datacenter</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {unexplainedRows.map((e) => (
                    <TableRow key={e.id} hover>
                      <TableCell>{e.name}</TableCell>
                      <TableCell sx={{ color: GREY }}>{e.dnsName}</TableCell>
                      <TableCell sx={{ color: GREY }}>{e.cluster}</TableCell>
                      <TableCell sx={{ color: GREY }}>{e.datacenter}</TableCell>
                    </TableRow>
                  ))}
                  {!coverage.unexplained.length && (
                    <TableRow><TableCell colSpan={4} sx={{ color: GREEN, fontWeight: 700 }}>
                      Every server carries at least one decoded token. That is not the same as every server being
                      mapped — confirm the mapping in Server mapping.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
